/**
 * Shared component renderers for flow pages.
 *
 * Static renderers — no data dependency, work identically on all pages.
 * Data-driven visualizations use self-contained components in ./self-contained/.
 */

import { pickT } from "@repo/shared";
import type {
  EmbedConfig,
  FileConfig,
  FlowPageComponent,
  IframeConfig,
  ImageConfig,
  ModalConfig,
  TextBlockConfig,
  YoutubeVideoConfig,
} from "@/lib/types/flow";
import { textBlockContent } from "@/lib/utils/text-block-content";
import { EmbedRenderer } from "./component-renderers/embed-renderer";
import { FileRenderer } from "./component-renderers/file-renderer";
import { FlowMarkdown } from "./component-renderers/flow-markdown";
import { IframeRenderer } from "./component-renderers/iframe-renderer";
import { ImageRenderer } from "./component-renderers/image-renderer";
import { ModalRenderer } from "./component-renderers/modal-renderer";
import { YoutubeVideoRenderer } from "./component-renderers/youtube-video-renderer";
import { T } from "./flow-component-renderers.i18n";

// ============================================
// Static Renderers (no data dependency)
// ============================================

/**
 * Renders a text block: markdown `content` whose headings all draw as the one
 * flow component header style (QL-296), so a text block heading and a widget
 * title on the same page are indistinguishable.
 */
export function renderTextBlock(component: FlowPageComponent, language?: string): React.ReactNode {
  const t = pickT(T, language || "en_GB");
  try {
    const config = (component.config ? JSON.parse(component.config) : {}) as TextBlockConfig;
    return <FlowMarkdown content={textBlockContent(config)} />;
  } catch {
    return <div className="text-sm text-destructive">{t.invalidConfiguration}</div>;
  }
}

/**
 * Renders a vertical spacer
 */
export function renderSpacer(component: FlowPageComponent): React.ReactNode {
  try {
    const config = component.config ? JSON.parse(component.config) : {};
    return <div style={{ height: `${config.height || 20}px` }} />;
  } catch {
    return null;
  }
}

/**
 * Renders a YouTube video embed
 */
export function renderYoutubeVideo(component: FlowPageComponent): React.ReactNode {
  try {
    const config = component.config ? JSON.parse(component.config) : {};
    return <YoutubeVideoRenderer config={config as YoutubeVideoConfig} />;
  } catch {
    return null;
  }
}

/**
 * Renders a third-party web page by https URL (QL-776)
 */
export function renderIframe(component: FlowPageComponent): React.ReactNode {
  try {
    const config = component.config ? JSON.parse(component.config) : {};
    return <IframeRenderer config={config as IframeConfig} />;
  } catch {
    return null;
  }
}

/**
 * Renders an image
 */
export function renderImage(component: FlowPageComponent): React.ReactNode {
  try {
    const config = component.config ? JSON.parse(component.config) : {};
    return <ImageRenderer config={config as ImageConfig} />;
  } catch {
    return null;
  }
}

/**
 * Renders an HTML embed
 */
export function renderEmbed(component: FlowPageComponent): React.ReactNode {
  try {
    const config = component.config ? JSON.parse(component.config) : {};
    return <EmbedRenderer config={config as EmbedConfig} />;
  } catch {
    return null;
  }
}

/**
 * Renders an info modal (button that opens a popup with markdown content)
 */
export function renderModal(component: FlowPageComponent, language?: string): React.ReactNode {
  try {
    const config = component.config ? JSON.parse(component.config) : {};
    return <ModalRenderer config={config as ModalConfig} language={language} />;
  } catch {
    return null;
  }
}

/**
 * Renders a file download link. On the public flow `FileRenderer` takes the
 * session token from the public session context.
 */
export function renderFile(component: FlowPageComponent, language?: string): React.ReactNode {
  try {
    const config = component.config ? JSON.parse(component.config) : {};
    return <FileRenderer config={config as FileConfig} language={language} />;
  } catch {
    return null;
  }
}
